import axios from "axios";
import api from "@/services/api";
import {
	ALL_ATTACHMENT_SLOTS,
	ATTACHMENT_TYPE_BY_SLOT,
	getAttachmentFileBySlot,
	hasAttachmentDesiredStateChanges,
	type InfractionAttachmentSlotKey,
	type InfractionAttachmentSlots,
	type InfractionEditAttachmentFormValues,
} from "../handlers/map-infraction-attachments-to-slots";
import type {
	AddInfractionAttachmentRequest,
	CreateManualInfractionApiResponse,
	CreateManualInfractionAttachmentRequest,
	CreateManualInfractionRequest,
	HabilitarMeIntegrationStatusResponse,
	InfractionAttachmentPresignedApiResponse,
	InfractionAttachmentPresignedRequest,
	InfractionAttachmentUrlApiResponse,
	InfractionAttachmentUrlsApiResponse,
	InfractionCatalogLookupApiResponse,
	InfractionDetailApiResponse,
	InfractionDriverLookupApiResponse,
	InfractionIssuingAuthorityLookupApiResponse,
	InfractionListPaginatedApiResponse,
	InfractionPaginatedRequest,
	InfractionSummaryApiResponse,
	InfractionVehicleLookupApiResponse,
	ReplaceInfractionAttachmentRequest,
	UpdateInfractionApiResponse,
	UpdateInfractionAttachmentRequest,
	UpdateInfractionRequest,
	UserProfileResponse,
} from "../types/infraction-api.types";
import { downloadFileFromUrl } from "../utils/download-file-from-url";

const plainAxios = axios.create();

type InfractionsSignInResponse = {
	redirectUrl: string;
};

export async function signInHabilitarMe(): Promise<string> {
	const response = await api.post<InfractionsSignInResponse>(
		"/api/habilitarme/sign-in"
	);

	if (!response.data.redirectUrl) {
		throw new Error("Não foi possível redirecionar para o Infrações.");
	}

	return response.data.redirectUrl;
}

export async function getInfractionsSummary(): Promise<InfractionSummaryApiResponse> {
	const { data } = await api.get<InfractionSummaryApiResponse>(
		"/api/infractions/summary"
	);
	return data;
}

export async function getInfractionsPaginated(
	params: InfractionPaginatedRequest
): Promise<InfractionListPaginatedApiResponse> {
	const { data } = await api.get<InfractionListPaginatedApiResponse>(
		"/api/infractions/paginated",
		{
			params: {
				...(params.search ? { Search: params.search } : {}),
				...(params.operationalStatuses?.length
					? { OperationalStatuses: params.operationalStatuses }
					: {}),
				...(params.deadlineStartDate
					? { DeadlineStartDate: params.deadlineStartDate }
					: {}),
				...(params.deadlineEndDate
					? { DeadlineEndDate: params.deadlineEndDate }
					: {}),
				PageNumber: params.pageNumber,
				PageSize: params.pageSize,
				...(params.sortBy ? { SortBy: params.sortBy } : {}),
				...(params.sortDirection
					? { SortDirection: params.sortDirection }
					: {}),
			},
			paramsSerializer: {
				indexes: null,
			},
		}
	);
	return data;
}

export async function getInfractionVehiclesLookup(): Promise<
	InfractionVehicleLookupApiResponse[]
> {
	const { data } = await api.get<InfractionVehicleLookupApiResponse[]>(
		"/api/vehicle/lookup"
	);
	return data;
}

export async function getDriverLookup(
	search?: string,
	limit = 50
): Promise<InfractionDriverLookupApiResponse[]> {
	const { data } = await api.get<InfractionDriverLookupApiResponse[]>(
		"/api/driver/lookup",
		{
			params: {
				...(search ? { Search: search } : {}),
				Limit: limit,
			},
		}
	);
	return data;
}

export function getInfractionDriversLookup(
	search?: string
): Promise<InfractionDriverLookupApiResponse[]> {
	return getDriverLookup(search);
}

export async function getInfractionIssuingAuthoritiesLookup(
	search?: string,
	limit = 50
): Promise<InfractionIssuingAuthorityLookupApiResponse[]> {
	const { data } = await api.get<InfractionIssuingAuthorityLookupApiResponse[]>(
		"/api/infractions/issuing-authorities/lookup",
		{
			params: {
				...(search ? { Search: search } : {}),
				Limit: limit,
			},
		}
	);
	return data;
}

export async function getInfractionCatalogLookup(
	search?: string,
	limit = 50
): Promise<InfractionCatalogLookupApiResponse[]> {
	const { data } = await api.get<InfractionCatalogLookupApiResponse[]>(
		"/api/infractions/catalog/lookup",
		{
			params: {
				...(search ? { Search: search } : {}),
				Limit: limit,
			},
		}
	);
	return data;
}

export async function getInfractionAttachmentsPresigned(
	payload: InfractionAttachmentPresignedRequest
): Promise<InfractionAttachmentPresignedApiResponse> {
	const { data } = await api.post<InfractionAttachmentPresignedApiResponse>(
		"/api/infractions/attachments/presigned",
		payload
	);
	return data;
}

export async function uploadInfractionFileToPresignedUrl(
	uploadUrl: string,
	file: File,
	contentType: string
): Promise<void> {
	await plainAxios.put(uploadUrl, file, {
		headers: { "Content-Type": contentType },
	});
}

export async function createManualInfraction(
	payload: CreateManualInfractionRequest
): Promise<CreateManualInfractionApiResponse> {
	const { data } = await api.post<CreateManualInfractionApiResponse>(
		"/api/infractions",
		payload
	);
	return data;
}

export async function uploadInfractionAttachments(
	vehicleId: number,
	files: { type: number; file: File }[]
): Promise<CreateManualInfractionAttachmentRequest[]> {
	if (files.length === 0) {
		return [];
	}

	const presignedResponse = await getInfractionAttachmentsPresigned({
		vehicleId,
		files: files.map((entry) => ({
			type: entry.type,
			fileName: entry.file.name,
			contentType: entry.file.type || "application/pdf",
		})),
	});

	const presignedFiles = presignedResponse.files ?? [];
	const uploadedAttachments: CreateManualInfractionAttachmentRequest[] = [];

	for (let index = 0; index < files.length; index++) {
		const sourceFile = files[index];
		const presignedFile = presignedFiles[index];

		if (!(presignedFile?.uploadUrl && presignedFile.fileKey)) {
			throw new Error("Não foi possível preparar o upload do anexo.");
		}

		await uploadInfractionFileToPresignedUrl(
			presignedFile.uploadUrl,
			sourceFile.file,
			presignedFile.contentType || sourceFile.file.type || "application/pdf"
		);

		uploadedAttachments.push({
			type: presignedFile.type,
			fileKey: presignedFile.fileKey,
			fileName: presignedFile.fileName || sourceFile.file.name,
			fileSize: sourceFile.file.size,
		});
	}

	return uploadedAttachments;
}

export async function uploadInfractionAttachmentForSlot(
	vehicleId: number,
	type: number,
	file: File
): Promise<CreateManualInfractionAttachmentRequest> {
	const uploadedAttachments = await uploadInfractionAttachments(vehicleId, [
		{ type, file },
	]);
	const uploadedAttachment = uploadedAttachments[0];

	if (!uploadedAttachment) {
		throw new Error("Não foi possível preparar o upload do anexo.");
	}

	return uploadedAttachment;
}

export async function addInfractionAttachment(
	infractionId: number,
	payload: AddInfractionAttachmentRequest
): Promise<void> {
	await api.post(`/api/infractions/${infractionId}/attachments`, payload);
}

export async function replaceInfractionAttachment(
	infractionId: number,
	attachmentId: number,
	payload: ReplaceInfractionAttachmentRequest
): Promise<void> {
	await api.put(
		`/api/infractions/${infractionId}/attachments/${attachmentId}`,
		payload
	);
}

export async function deleteInfractionAttachment(
	infractionId: number,
	attachmentId: number
): Promise<void> {
	await api.delete(
		`/api/infractions/${infractionId}/attachments/${attachmentId}`
	);
}

type BuildDesiredAttachmentsStateParams = {
	vehicleId: number;
	values: InfractionEditAttachmentFormValues;
	originalSlots: InfractionAttachmentSlots;
	removedSlotKeys: ReadonlySet<InfractionAttachmentSlotKey>;
};

/**
 * Builds the desired-state attachments payload for PUT /infractions/{id}.
 * Uses fileKey/type from GET detail — does not call attachments/urls.
 * Returns null when attachments must not be changed.
 */
export async function buildDesiredAttachmentsState({
	vehicleId,
	values,
	originalSlots,
	removedSlotKeys,
}: BuildDesiredAttachmentsStateParams): Promise<
	UpdateInfractionAttachmentRequest[] | null
> {
	if (
		!hasAttachmentDesiredStateChanges(values, originalSlots, removedSlotKeys)
	) {
		return null;
	}

	const desired: UpdateInfractionAttachmentRequest[] = [];

	for (const slotKey of ALL_ATTACHMENT_SLOTS) {
		const type = ATTACHMENT_TYPE_BY_SLOT[slotKey];
		const original = originalSlots[slotKey];
		const newFile = getAttachmentFileBySlot(values, slotKey);
		const isRemoved = removedSlotKeys.has(slotKey);

		if (newFile) {
			const uploaded = await uploadInfractionAttachmentForSlot(
				vehicleId,
				type,
				newFile
			);

			desired.push({
				...(original ? { id: original.id } : {}),
				type,
				fileKey: uploaded.fileKey,
				fileName: uploaded.fileName,
				fileSize: newFile.size,
			});
			continue;
		}

		if (isRemoved || !original) {
			continue;
		}

		if (!original.fileKey) {
			throw new Error(
				"Não foi possível manter o anexo existente. Recarregue a infração e tente novamente."
			);
		}

		desired.push({
			id: original.id,
			type: original.type,
			fileKey: original.fileKey,
			fileName: original.fileName,
			fileSize: original.fileSize,
		});
	}

	return desired;
}

export async function getInfractionById(
	infractionId: number
): Promise<InfractionDetailApiResponse> {
	const { data } = await api.get<InfractionDetailApiResponse>(
		`/api/infractions/${infractionId}`
	);
	return data;
}

export async function updateInfraction(
	infractionId: number,
	payload: UpdateInfractionRequest
): Promise<UpdateInfractionApiResponse> {
	const { data } = await api.put<UpdateInfractionApiResponse>(
		`/api/infractions/${infractionId}`,
		payload
	);
	return data;
}

export async function getInfractionAttachmentUrls(
	infractionId: number
): Promise<InfractionAttachmentUrlsApiResponse> {
	const { data } = await api.get<InfractionAttachmentUrlsApiResponse>(
		`/api/infractions/${infractionId}/attachments/urls`
	);
	return data;
}

export async function getInfractionAttachmentUrl(
	infractionId: number,
	attachmentId: number
): Promise<InfractionAttachmentUrlApiResponse> {
	const batch = await getInfractionAttachmentUrls(infractionId);
	const attachment = (batch.attachments ?? []).find(
		(item) => item.attachmentId === attachmentId
	);

	if (!attachment) {
		throw new Error("Arquivo da infração não encontrado.");
	}

	return attachment;
}

export async function downloadInfractionAttachment(
	infractionId: number,
	attachmentId: number
): Promise<void> {
	const { url, fileName } = await getInfractionAttachmentUrl(
		infractionId,
		attachmentId
	);

	if (!url) {
		throw new Error("URL do anexo não disponível.");
	}

	await downloadFileFromUrl(url, fileName || "anexo-infraction.pdf");
}

export async function getHabilitarMeStatus(): Promise<HabilitarMeIntegrationStatusResponse> {
	const { data } = await api.get<HabilitarMeIntegrationStatusResponse>(
		"/api/habilitarme/status"
	);
	return data;
}

export async function getUserProfile(): Promise<UserProfileResponse> {
	const { data } = await api.get<UserProfileResponse>("/api/auth/user/profile");
	return data;
}

export async function markInfractionOnboardingSeen(): Promise<void> {
	await api.put("/api/auth/user/onboarding/infractions/seen");
}

export async function downloadInfractionAttachments(
	infractionId: number
): Promise<void> {
	const batch = await getInfractionAttachmentUrls(infractionId);
	const attachments = (batch.attachments ?? []).filter((attachment) =>
		Boolean(attachment.url)
	);

	if (attachments.length === 0) {
		throw new Error("Nenhum anexo disponível para download.");
	}

	for (let index = 0; index < attachments.length; index++) {
		const attachment = attachments[index];
		if (!attachment?.url) {
			continue;
		}

		await downloadFileFromUrl(
			attachment.url,
			attachment.fileName || `anexo-infraction-${index + 1}.pdf`
		);

		// Small gap so the browser does not block consecutive download clicks.
		if (index < attachments.length - 1) {
			await new Promise((resolve) => {
				setTimeout(resolve, 250);
			});
		}
	}
}
